import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

interface Student {
    id: number;
    name: string;
    date_reg: string;
    guardian_number: string;
    branch: number;
    class_associated: number;
    section_associated: number;
    class_name: string;
    address: string;
    district: string;
    image: string;
    section_name: string;
    Index_number: string | null;
    gender?: string;
    nameOf_parent?: string;
    date_of_birth?: string;
    admission_number?: string;
}

const StudentDetails: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();

    const [student, setStudent] = useState<Student | null>(null);
    const [userRole, setUserRole] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [editable, setEditable] = useState<boolean>(false);
    const [classes, setClasses] = useState<any[]>([]);
    const [sections, setSections] = useState<any[]>([]);

    const [selectedImage, setSelectedImage] = useState<File | null>(null);

    useEffect(() => {
        const fetchStudentDetails = async () => {
            try {
                const response = await axios.get(`/Branches/students/${id}/`);
                setStudent(response.data);
            } catch (err) {
                console.error('Error fetching student details:', err);
                setError('Failed to fetch student details.');
            } finally {
                setLoading(false);
            }
        };

        const fetchUserRole = async () => {
            try {
                const { data } = await axios.get('/users/user');
                setUserRole(data.user.role);
            } catch (error) {
                console.error('Failed to fetch user role:', error);
                setError('Failed to fetch user role');
            }
        };

        const fetchClassesAndSections = async () => {
            try {
                const [classesRes, sectionsRes] = await Promise.all([
                    axios.get('/Branches/classes/'),
                    axios.get('/Branches/sections/')
                ]);

                setClasses(classesRes.data.results || classesRes.data);
                setSections(sectionsRes.data.results || sectionsRes.data);
            } catch (err) {
                console.error('Error fetching classes/sections:', err);
            }
        };

        fetchStudentDetails();
        fetchUserRole();
        fetchClassesAndSections();
    }, [id]);

    const handleDelete = async () => {
        if (window.confirm('Ma hubtaa inaad rabto inaad tirtirto ardaygan?')) {
            try {
                await axios.delete(`/Branches/students/${id}/`);
                navigate('/students');
            } catch (err) {
                console.error('Error deleting student:', err);
                setError('Failed to delete student.');
            }
        }
    };

    const handleEditToggle = () => {
        setEditable(!editable);
        setSelectedImage(null); // Reset image selection on toggle
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setStudent({
            ...student,
            [name]: value,
        } as Student);
    };

    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            setSelectedImage(e.target.files[0]);
        }
    };

    const validateForm = () => {
        if (!student?.name || !student?.guardian_number) {
            toast.error("Fadlan buuxi dhamaan meelaha banaan ee loo baahan yahay.");
            return false;
        }
        if (student.name.trim().length < 3) {
            toast.error("Magaca ardayga waa inuu ka yaraan 3 xaraf.");
            return false;
        }
        if (student.guardian_number) {
            const phoneDigits = student.guardian_number.replace(/\D/g, '');
            if (phoneDigits.length < 9 || phoneDigits.length > 15) {
                toast.error("Lambarka masuulka waa inuu u dhaxeeyaa 9 ilaa 15 nambar.");
                return false;
            }
        }
        return true;
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (!validateForm()) return;

        setLoading(true);
        try {
            const formData = new FormData();
            formData.append('name', student?.name || '');
            formData.append('guardian_number', student?.guardian_number || '');
            formData.append('address', student?.address || '');
            formData.append('district', student?.district || '');
            formData.append('class_associated', String(student?.class_associated || ''));
            formData.append('section_associated', String(student?.section_associated || ''));
            formData.append('gender', student?.gender || '');
            formData.append('nameOf_parent', student?.nameOf_parent || '');

            // Only append these if they exist and are not null
            if (student?.date_of_birth) formData.append('date_of_birth', student.date_of_birth);
            if (student?.admission_number) formData.append('admission_number', student.admission_number);
            if (student?.Index_number) formData.append('Index_number', student.Index_number);

            // Append image if selected
            if (selectedImage) {
                formData.append('image', selectedImage);
            }

            await axios.put(`/Branches/students/${id}/`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            toast.success('Ardayga si guul leh ayaa loo cusbooneysiiyay.');
            setEditable(false);
            setSelectedImage(null);

            // Refresh student data
            const response = await axios.get(`/Branches/students/${id}/`);
            setStudent(response.data);

        } catch (err: any) {
            console.error('Error updating student:', err);

            // Extract detailed error messages from backend response
            let errorMsg = 'Waa la guuldareystay cusbooneysiinta ardayga. Fadlan isku day mar kale.';

            if (err.response?.data) {
                const backendError = err.response.data;

                // Handle field-specific validation errors
                if (typeof backendError === 'object' && !backendError.detail) {
                    const fieldErrors: string[] = [];
                    Object.keys(backendError).forEach((field) => {
                        const messages = Array.isArray(backendError[field])
                            ? backendError[field]
                            : [backendError[field]];
                        fieldErrors.push(`${field}: ${messages.join(', ')}`);
                    });
                    errorMsg = fieldErrors.length > 0
                        ? `Validation errors: ${fieldErrors.join('; ')}`
                        : errorMsg;
                }
                // Handle general error message
                else if (backendError.detail) {
                    errorMsg = String(backendError.detail);
                }
                // Handle string error messages
                else if (typeof backendError === 'string') {
                    errorMsg = backendError;
                }
            }
            // Handle network errors
            else if (err.message) {
                errorMsg = `Network error: ${err.message}`;
            }

            toast.error(errorMsg, { autoClose: 5000 });
            setError(errorMsg);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: 'calc(100vh - 60px)' }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Soo kabanaya...</span>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="alert alert-danger mx-3 mt-3" role="alert">
                Cillad: {error}
            </div>
        );
    }

    if (!student) {
        return (
            <div className="alert alert-warning mx-3 mt-3" role="alert">
                Ardayga lama helin.
            </div>
        );
    }

    return (
        <div className="dashboard-content">
            <ToastContainer position="top-right" autoClose={3000} theme="colored" />
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h2 className="fw-bold text-primary">Profile-ka Ardayda</h2>
                <div>
                    {userRole !== 'accountant' && userRole !== 'teacher' && (
                        <button
                            className="btn btn-outline-danger me-2"
                            onClick={handleDelete}
                        >
                            <i className="bi bi-trash-fill me-1"></i>Tirtir
                        </button>
                    )}
                    {userRole !== 'teacher' && (
                        <button
                            className={`btn ${editable ? 'btn-outline-secondary' : 'btn-primary'}`}
                            onClick={handleEditToggle}
                        >
                            <i className={`bi ${editable ? 'bi-x-lg' : 'bi-pencil-fill'} me-1`}></i>
                            {editable ? 'Iska Daay' : 'Wax ka bedel'}
                        </button>
                    )}
                </div>
            </div>

            <div className="row g-4">
                {/* Student Profile Card */}
                <div className="col-lg-4">
                    <div className="card-modern h-100" style={{ borderRadius: '16px' }}>
                        <div className="card-body p-4 text-center">
                            {/* Profile Image */}
                            <div className="position-relative mb-3 mx-auto" style={{ width: '160px', height: '160px' }}>
                                {student.image ? (
                                    <img
                                        src={student.image}
                                        alt="Student"
                                        className="img-fluid rounded-circle border border-4 border-white shadow-sm w-100 h-100"
                                        style={{ objectFit: 'cover' }}
                                    />
                                ) : (
                                    <div className="bg-light rounded-circle d-flex align-items-center justify-content-center w-100 h-100 border border-4 border-white shadow-sm">
                                        <i className="bi bi-person-fill text-secondary" style={{ fontSize: '4.5rem' }}></i>
                                    </div>
                                )}
                                <div className="position-absolute bottom-0 end-0 bg-primary rounded-circle p-2 border border-3 border-white">
                                    <i className="bi bi-patch-check-fill text-white" style={{ fontSize: '1.2rem' }}></i>
                                </div>
                            </div>

                            {/* Student Name and ID */}
                            <h3 className="fw-bold mb-1">{student.name}</h3>
                            <p className="text-muted mb-3">Aqoonsi: {id}</p>

                            {/* Class Badges */}
                            <div className="d-flex justify-content-center gap-2 mb-4 flex-wrap">
                                <span className="badge bg-primary bg-opacity-10 text-primary py-2 px-3 rounded-pill">
                                    <i className="bi bi-book me-1"></i> {student.class_name}
                                </span>
                                <span className="badge bg-info bg-opacity-10 text-info py-2 px-3 rounded-pill">
                                    <i className="bi bi-collection me-1"></i> {student.section_name}
                                </span>
                            </div>

                            {/* Student Info List */}
                            <div className="list-group list-group-flush text-start bg-transparent">
                                <div className="list-group-item bg-transparent d-flex justify-content-between align-items-center px-0 py-2 border-bottom">
                                    <span className="d-flex align-items-center">
                                        <i className="bi bi-hash me-2 text-primary"></i>
                                        <span className="text-muted">Lambarka Tixraaca</span>
                                    </span>
                                    <span className="fw-semibold">{student.Index_number || 'N/A'}</span>
                                </div>

                                <div className="list-group-item bg-transparent d-flex justify-content-between align-items-center px-0 py-2 border-bottom">
                                    <span className="d-flex align-items-center">
                                        <i className="bi bi-calendar-event me-2 text-primary"></i>
                                        <span className="text-muted">Ayaa Diiwaangashan</span>
                                    </span>
                                    <span className="fw-semibold">{student.date_reg}</span>
                                </div>

                                <div className="list-group-item bg-transparent d-flex justify-content-between align-items-center px-0 py-2 border-bottom">
                                    <span className="d-flex align-items-center">
                                        <i className="bi bi-telephone me-2 text-primary"></i>
                                        <span className="text-muted">Masuulka</span>
                                    </span>
                                    <span className="fw-semibold">{student.guardian_number}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Student Details Card */}
                <div className="col-lg-8">
                    <div className="card-modern h-100" style={{ borderRadius: '16px' }}>
                        <div className="card-body p-4">
                            {editable ? (
                                <form onSubmit={handleSubmit}>
                                    <div className="d-flex align-items-center mb-4">
                                        <div className="bg-primary bg-opacity-10 p-2 rounded me-3">
                                            <i className="bi bi-pencil-square text-primary fs-5"></i>
                                        </div>
                                        <h5 className="mb-0 text-primary fw-bold">Wax ka bedel Xogta Ardayga</h5>
                                    </div>

                                    {error && (
                                        <div className="alert alert-danger mb-4" role="alert">
                                            <i className="bi bi-exclamation-triangle-fill me-2"></i>
                                            {error}
                                        </div>
                                    )}

                                    {/* Image Update Input */}
                                    <div className="mb-4">
                                        <label htmlFor="image" className="form-label fw-semibold">Cusbooneysii Sawirka Profile-ka</label>
                                        <div className="input-group">
                                            <span className="input-group-text bg-light">
                                                <i className="bi bi-camera text-muted"></i>
                                            </span>
                                            <input
                                                type="file"
                                                className="form-control"
                                                id="image"
                                                onChange={handleImageChange}
                                                accept="image/*"
                                            />
                                        </div>
                                        <div className="form-text">Faaruq kaga tag si aad u haysato sawirka hadda</div>
                                    </div>

                                    <div className="row g-3 mb-3">
                                        <div className="col-md-6">
                                            <label htmlFor="name" className="form-label fw-semibold">Magaca Oo Buuxa *</label>
                                            <div className="input-group">
                                                <span className="input-group-text bg-light">
                                                    <i className="bi bi-person text-muted"></i>
                                                </span>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    id="name"
                                                    name="name"
                                                    value={student.name}
                                                    onChange={handleChange}
                                                    required
                                                />
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <label htmlFor="guardian_number" className="form-label fw-semibold">Lambarka Masuulka *</label>
                                            <div className="input-group">
                                                <span className="input-group-text bg-light">
                                                    <i className="bi bi-telephone text-muted"></i>
                                                </span>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    id="guardian_number"
                                                    name="guardian_number"
                                                    value={student.guardian_number}
                                                    onChange={handleChange}
                                                    required
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="row g-3 mb-3">
                                        <div className="col-md-6">
                                            <label htmlFor="nameOf_parent" className="form-label fw-semibold">Magaca Masuulka</label>
                                            <div className="input-group">
                                                <span className="input-group-text bg-light">
                                                    <i className="bi bi-person-badge text-muted"></i>
                                                </span>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    id="nameOf_parent"
                                                    name="nameOf_parent"
                                                    value={student.nameOf_parent || ''}
                                                    onChange={handleChange}
                                                />
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <label htmlFor="gender" className="form-label fw-semibold">Jinsiga</label>
                                            <select
                                                className="form-select"
                                                id="gender"
                                                name="gender"
                                                value={student.gender || ''}
                                                onChange={handleChange}
                                            >
                                                <option value="">Dooro Jinsiga</option>
                                                <option value="Male">Lab</option>
                                                <option value="Female">Dheddig</option>
                                                <option value="Other">Kale</option>
                                            </select>
                                        </div>
                                    </div>

                                    <div className="row g-3 mb-3">
                                        <div className="col-md-6">
                                            <label htmlFor="class_associated" className="form-label fw-semibold">Fasalka</label>
                                            <select
                                                className="form-select"
                                                id="class_associated"
                                                name="class_associated"
                                                value={student.class_associated}
                                                onChange={handleChange}
                                            >
                                                <option value="">Dooro Fasalka</option>
                                                {classes.map(cls => (
                                                    <option key={cls.id} value={cls.id}>
                                                        {cls.name}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="col-md-6">
                                            <label htmlFor="section_associated" className="form-label fw-semibold">Qeybta/Fasalka</label>
                                            <select
                                                className="form-select"
                                                id="section_associated"
                                                name="section_associated"
                                                value={student.section_associated}
                                                onChange={handleChange}
                                            >
                                                <option value="">Dooro Qeybta</option>
                                                {sections
                                                    .filter(section => section.class_associated === student.class_associated)
                                                    .map(section => (
                                                        <option key={section.id} value={section.id}>
                                                            {section.name}
                                                        </option>
                                                    ))}
                                            </select>
                                        </div>
                                    </div>

                                    <div className="row g-3 mb-3">
                                        <div className="col-md-6">
                                            <label htmlFor="Index_number" className="form-label fw-semibold">Lambarka Tixraaca</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                id="Index_number"
                                                name="Index_number"
                                                value={student.Index_number || ''}
                                                onChange={handleChange}
                                            />
                                        </div>
                                    </div>
                                    <div className="mb-3">
                                        <label htmlFor="address" className="form-label fw-semibold">Cinwaanka *</label>
                                        <textarea
                                            className="form-control"
                                            id="address"
                                            name="address"
                                            value={student.address}
                                            onChange={handleChange}
                                            rows={2}
                                            required
                                        />
                                    </div>

                                    <div className="row g-3 mb-3">
                                        <div className="col-md-6">
                                            <label htmlFor="district" className="form-label fw-semibold">Degmada *</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                id="district"
                                                name="district"
                                                value={student.district || ''}
                                                onChange={handleChange}
                                                required
                                            />
                                        </div>

                                    </div>
                                    <div className="d-flex justify-content-end pt-2">
                                        <button type="submit" className="btn btn-primary px-4 py-2 d-flex align-items-center">
                                            <i className="bi bi-check-circle-fill me-2"></i>
                                            Badbaadi Isbedelada
                                        </button>
                                    </div>
                                </form>
                            ) : (
                                <>
                                    <div className="d-flex align-items-center mb-4">
                                        <div className="bg-primary bg-opacity-10 p-2 rounded me-3">
                                            <i className="bi bi-info-circle text-primary fs-5"></i>
                                        </div>
                                        <h5 className="mb-0 text-primary fw-bold">Faahfaahinta Ardayga</h5>
                                    </div>

                                    <div className="row g-4">
                                        <div className="col-md-6">
                                            <div className="bg-light p-3 rounded">
                                                <h6 className="d-flex align-items-center text-muted mb-3">
                                                    <i className="bi bi-house-door me-2"></i>
                                                    Cinwaanka
                                                </h6>
                                                <p className="fw-normal mb-0">{student.address}</p>
                                            </div>
                                        </div>

                                        <div className="col-md-6">
                                            <div className="bg-light p-3 rounded">
                                                <h6 className="d-flex align-items-center text-muted mb-3">
                                                    <i className="bi bi-geo-alt me-2"></i>
                                                    Degmada
                                                </h6>
                                                <span className="badge bg-secondary bg-opacity-10 text-dark py-2 px-3 rounded-pill d-inline-flex align-items-center">
                                                    {student.district || 'N/A'}
                                                </span>
                                            </div>
                                        </div>

                                        <div className="col-md-6">
                                            <div className="bg-light p-3 rounded">
                                                <h6 className="d-flex align-items-center text-muted mb-3">
                                                    <i className="bi bi-book me-2"></i>
                                                    Xogta Fasalka
                                                </h6>
                                                <div className="d-flex align-items-center flex-wrap gap-2">
                                                    <span className="badge bg-primary bg-opacity-10 text-primary py-2 px-3 rounded-pill">
                                                        {student.class_name}
                                                    </span>
                                                    <small className="text-muted">(ID: {student.class_associated})</small>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="col-md-6">
                                            <div className="bg-light p-3 rounded">
                                                <h6 className="d-flex align-items-center text-muted mb-3">
                                                    <i className="bi bi-diagram-2 me-2"></i>
                                                    Qeybta
                                                </h6>
                                                <span className="badge bg-info bg-opacity-10 text-info py-2 px-3 rounded-pill">
                                                    {student.section_name}
                                                </span>
                                            </div>
                                        </div>

                                        {student.gender && (
                                            <div className="col-md-6">
                                                <div className="bg-light p-3 rounded">
                                                    <h6 className="d-flex align-items-center text-muted mb-3">
                                                        <i className="bi bi-gender-ambiguous me-2"></i>
                                                        Jinsiga
                                                    </h6>
                                                    <span className="badge bg-info bg-opacity-10 text-info py-2 px-3 rounded-pill">
                                                        {student.gender}
                                                    </span>
                                                </div>
                                            </div>
                                        )}

                                        {student.nameOf_parent && (
                                            <div className="col-md-6">
                                                <div className="bg-light p-3 rounded">
                                                    <h6 className="d-flex align-items-center text-muted mb-3">
                                                        <i className="bi bi-person-badge me-2"></i>
                                                        Magaca Masuulka
                                                    </h6>
                                                    <span className="badge bg-info bg-opacity-10 text-info py-2 px-3 rounded-pill">
                                                        {student.nameOf_parent}
                                                    </span>
                                                </div>
                                            </div>
                                        )}

                                        {student.Index_number && (
                                            <div className="col-md-6">
                                                <div className="bg-light p-3 rounded">
                                                    <h6 className="d-flex align-items-center text-muted mb-3">
                                                        <i className="bi bi-hash me-2"></i>
                                                        Lambarka Tixraaca
                                                    </h6>
                                                    <span className="badge bg-secondary bg-opacity-10 text-secondary py-2 px-3 rounded-pill">
                                                        {student.Index_number}
                                                    </span>
                                                </div>
                                            </div>
                                        )}

                                    </div>

                                    <div className="mt-4">
                                        <div className="bg-primary bg-opacity-5 p-3 rounded border-start border-primary border-4">
                                            <div className="d-flex align-items-center">
                                                <i className="bi bi-info-circle-fill text-primary fs-5 me-3"></i>
                                                <div>
                                                    <h6 className="mb-1 text-white">Xog Dheeraad Ah</h6>
                                                    <p className="small text-white mb-0">
                                                        Wixii diiwaanno faahfaahsan, fadlan fiiri warbixinnada tacliinta iyo xaadirinta ardayga.
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default StudentDetails;